import { assertNever } from "@@/shared/assert-never";
import { compare } from "@@/shared/comparisons";
import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { Order } from "@@/shared/orders/order";
import { emptyArrayOf } from "@@/utilities/empty";
import type { Except } from "type-fest";
import { z } from "zod";
import type {
    FeeSourceOrdersConditionId} from "./conditions/fee-source-orders-condition-id";
import {
    feeSourceOrdersConditionIdZodSchema,
} from "./conditions/fee-source-orders-condition-id";
import type {
    FeeSourceOrdersCondition} from "./conditions/fee-source-orders-conditions";
import {
    feeSourceOrdersConditionZodSchema,
} from "./conditions/fee-source-orders-conditions";
import type {
    FeeSourceOrdersId} from "./fee-source-orders-id";
import {
    feeSourceOrdersIdFactory,
    feeSourceOrdersIdZodSchema,
} from "./fee-source-orders-id";
import type {
    FeeSourceOrdersFilterId} from "./filters/fee-source-orders-filter-id";
import {
    feeSourceOrdersFilterIdZodSchema,
} from "./filters/fee-source-orders-filter-id";
import type {
    FeeSourceOrdersFilter} from "./filters/fee-source-orders-filters";
import {
    feeSourceOrdersFilterZodSchema,
} from "./filters/fee-source-orders-filters";

/**
 * Fee source for managing fees on order data \
 * E.g. fees for orders with a special delivery type, or orders bought on a specific destination etc.
 */
type FeeSourceOrders = {
    readonly _id: FeeSourceOrdersId;
    readonly _type: "FEE_SOURCE";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly type: "ORDERS";
    readonly conditions: Record<
        FeeSourceOrdersConditionId,
        FeeSourceOrdersCondition
    >;
    readonly conditionOrder: FeeSourceOrdersConditionId[];
    readonly filters: Record<FeeSourceOrdersFilterId, FeeSourceOrdersFilter>;
    readonly filterOrder: FeeSourceOrdersFilterId[];
};

type Params = Except<FeeSourceOrders, "_id" | "type" | "_type" | "_createdAt">;
const feeSourceOrdersFactory = (params: Params): FeeSourceOrders => {
    return {
        ...params,
        _id: feeSourceOrdersIdFactory(),
        _type: "FEE_SOURCE",
        _createdAt: nowIsoAndUnix(),
        type: "ORDERS",
    };
};

const feeSourceOrdersZodSchema: z.ZodSchema<FeeSourceOrders> = z.object({
    _id: feeSourceOrdersIdZodSchema,
    _type: z.literal("FEE_SOURCE"),
    _createdAt: isoAndUnixZodSchema,
    type: z.literal("ORDERS"),
    conditions: z.record(
        feeSourceOrdersConditionIdZodSchema,
        feeSourceOrdersConditionZodSchema,
    ) as z.ZodSchema<FeeSourceOrders["conditions"]>, // quickfix zod issue
    conditionOrder: z.array(feeSourceOrdersConditionIdZodSchema),
    filters: z.record(
        feeSourceOrdersFilterIdZodSchema,
        feeSourceOrdersFilterZodSchema,
    ) as z.ZodSchema<FeeSourceOrders["filters"]>, // quickfix zod issue,
    filterOrder: z.array(feeSourceOrdersFilterIdZodSchema),
});

/**
 * Returns all orders of given order avaialable to the fee source parameters
 * @param {Order[]} orders
 * @param {FeeSourceOrders} feeSource
 * @return {*}  {Order[]}
 */
const feeSourceOrders = (
    orders: Order[],
    feeSource: FeeSourceOrders,
): Order[] => {
    const filters = feeSource.filterOrder.map(
        filterId => feeSource.filters[filterId],
    );
    const conditions = feeSource.conditionOrder.map(
        conditionalId => feeSource.conditions[conditionalId],
    );

    const filteredOrders = filters.reduce(
        (result, filter) => {
            const filtered = result.filter(order => {
                const filterType = filter.type;
                const invert = "invert" in filter ? filter.invert : false;
                let filterMatch = false;
                switch (filterType) {
                    case "DELIVERY_TYPE":
                        filterMatch =
                            order.delivery.deliveryMethodType ===
                            filter.deliveryType;
                        break;
                    case "DESTINATION": {
                        filterMatch = order.destination === filter.destination;
                        break;
                    }
                    case "PAYMENT_PROVIDER":
                        filterMatch = false;
                        // TODO: GET PAYMENT PROVIDERS FOR ORDER
                        break;
                    case "PRICE": {
                        filterMatch = compare(filter.comparison)(
                            filter.orderPrice.amountIncludingVat,
                            order.totalPrice.amountIncludingVat,
                        );
                        break;
                    }
                    default:
                        assertNever(filterType);
                }
                const match = invert ? !filterMatch : filterMatch;
                return match;
            });
            return filtered;
        },
        [...orders],
    );

    const conditionsMet = conditions.every(condition => {
        switch (condition.type) {
            case "COUNT":
                return compare(condition.comparison)(
                    condition.value,
                    filteredOrders.length,
                );
            default:
                assertNever(condition.type);
                return false;
        }
    });

    if (!conditionsMet) {
        return emptyArrayOf<Order>();
    }

    return filteredOrders;
};

export { feeSourceOrders, feeSourceOrdersFactory, feeSourceOrdersZodSchema };
export type { FeeSourceOrders };
